import React, { useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import moment from 'moment';

import Text from '../global/Text';
import fontMaker from '../../utils/font';
import { colors } from '../../constants/theme';

export type ScheduleCalendarMarkedDate = {
  activeCount: number;
  totalCount: number;
};

interface ScheduleCalendarProps {
  currentMonth: number;
  selectedDate: string;
  markedDates: Record<string, ScheduleCalendarMarkedDate>;
  minDate?: string | null;
  maxDate?: string | null;
  onSelectDate: (date: string) => void;
  onChangeMonth: (direction: 'prev' | 'next') => void;
}

const MARKER_COLOR = '#F5BE3D';

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  currentMonth,
  selectedDate,
  markedDates,
  minDate,
  maxDate,
  onSelectDate,
  onChangeMonth
}) => {
  const monthMoment = useMemo(() => moment(currentMonth).startOf('month'), [currentMonth]);
  const minMonth = useMemo(() => (minDate ? moment(minDate).startOf('month') : null), [minDate]);
  const maxMonth = useMemo(() => (maxDate ? moment(maxDate).startOf('month') : null), [maxDate]);

  const canGoPrevMonth = !minMonth || monthMoment.isAfter(minMonth, 'month');
  const canGoNextMonth = !maxMonth || monthMoment.isBefore(maxMonth, 'month');

  const calendarData = useMemo(() => {
    const monthStart = monthMoment.clone().startOf('month');
    const monthEnd = monthMoment.clone().endOf('month');

    const gridStart = monthStart.clone().day(0);
    if (gridStart.isAfter(monthStart, 'day')) {
      gridStart.subtract(7, 'days');
    }

    const gridEnd = monthEnd.clone().day(6);
    if (gridEnd.isBefore(monthEnd, 'day')) {
      gridEnd.add(7, 'days');
    }

    const weeks: moment.Moment[][] = [];
    const cursor = gridStart.clone();

    while (cursor.isSameOrBefore(gridEnd, 'day')) {
      const week: moment.Moment[] = [];
      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        week.push(cursor.clone());
        cursor.add(1, 'day');
      }
      weeks.push(week);
    }

    return weeks;
  }, [monthMoment]);

  const weekdayLabels = useMemo(() => {
    const start = moment().day(0);
    return Array.from({ length: 7 }, (_, index) => start.clone().add(index, 'day').format('ddd'));
  }, []);

  const today = moment().locale('en').format('YYYY-MM-DD');

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <TouchableOpacity
          activeOpacity={0.7}
          disabled={!canGoPrevMonth}
          onPress={() => onChangeMonth('prev')}
          style={[styles.navButton, !canGoPrevMonth ? styles.navButtonDisabled : null]}
        >
          <Icon name="chevron-left" size={22} color={canGoPrevMonth ? colors.darkgray : colors.lightgray} />
        </TouchableOpacity>
        <Text style={styles.monthText}>{monthMoment.format('MMMM YYYY')}</Text>
        <TouchableOpacity
          activeOpacity={0.7}
          disabled={!canGoNextMonth}
          onPress={() => onChangeMonth('next')}
          style={[styles.navButton, !canGoNextMonth ? styles.navButtonDisabled : null]}
        >
          <Icon name="chevron-right" size={22} color={canGoNextMonth ? colors.darkgray : colors.lightgray} />
        </TouchableOpacity>
      </View>

      <View style={styles.weekHeaderRow}>
        {weekdayLabels.map(label => (
          <View key={label} style={styles.weekHeaderCell}>
            <Text style={styles.weekHeaderText}>{label}</Text>
          </View>
        ))}
      </View>

      {calendarData.map((week, weekIndex) => (
        <View key={`calendar-week-${weekIndex}`} style={styles.weekRow}>
          {week.map(day => {
            const dateKey = day.format('YYYY-MM-DD');
            const marker = markedDates[dateKey];
            const isCurrentMonth = day.isSame(monthMoment, 'month');
            const isSelected = dateKey === selectedDate;
            const isToday = dateKey === today;
            const isDisabled =
              !isCurrentMonth ||
              (!!minDate && day.isBefore(minDate, 'day')) ||
              (!!maxDate && day.isAfter(maxDate, 'day'));
            const hasSchedules = (marker?.totalCount ?? 0) > 0;
            const hasActiveSchedules = (marker?.activeCount ?? 0) > 0;

            if (!isCurrentMonth) {
              return <View key={dateKey} style={styles.dayCell} />;
            }

            return (
              <View key={dateKey} style={styles.dayCell}>
                <TouchableOpacity
                  activeOpacity={isDisabled ? 1 : 0.8}
                  disabled={isDisabled}
                  onPress={() => onSelectDate(dateKey)}
                  style={[
                    styles.dayButton,
                    isSelected ? styles.dayButtonSelected : null,
                    isToday && !isSelected ? styles.dayButtonToday : null,
                    isDisabled ? styles.dayButtonDisabled : null
                  ]}
                >
                  {hasSchedules && (
                    <View
                      style={[
                        styles.marker,
                        !hasActiveSchedules ? styles.markerInactive : null,
                        isSelected ? styles.markerSelected : null
                      ]}
                    />
                  )}
                  <Text
                    style={[
                      styles.dayText,
                      isSelected ? styles.dayTextSelected : null,
                      isToday && !isSelected ? styles.dayTextToday : null,
                      isDisabled ? styles.dayTextDisabled : null
                    ]}
                  >
                    {day.format('D')}
                  </Text>
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
};

export default React.memo(ScheduleCalendar);

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginBottom: 10,
    paddingHorizontal: 4,
    paddingTop: 0,
    paddingBottom: 2,
    backgroundColor: '#fff'
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  navButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center'
  },
  navButtonDisabled: {
    opacity: 0.4
  },
  monthText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 16,
    color: '#25282B',
    textTransform: 'capitalize'
  },
  weekHeaderRow: {
    flexDirection: 'row',
    marginBottom: 4
  },
  weekHeaderCell: {
    flex: 1,
    alignItems: 'center'
  },
  weekHeaderText: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 11,
    color: colors.gray,
    textTransform: 'capitalize'
  },
  weekRow: {
    flexDirection: 'row',
    marginBottom: 6
  },
  dayCell: {
    flex: 1,
    paddingHorizontal: 2
  },
  dayButton: {
    height: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E9ECEF',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    position: 'relative'
  },
  dayButtonSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    shadowColor: colors.primary,
    shadowOpacity: 0.18,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 2
  },
  dayButtonToday: {
    borderColor: colors.primary,
    backgroundColor: '#F1FCF5'
  },
  dayButtonDisabled: {
    backgroundColor: '#F8F9FA',
    borderColor: '#F1F3F5'
  },
  dayText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 14,
    color: colors.tertiary
  },
  dayTextSelected: {
    color: '#fff'
  },
  dayTextToday: {
    color: colors.primaryDark
  },
  dayTextDisabled: {
    color: colors.lightgray
  },
  marker: {
    position: 'absolute',
    top: 5,
    right: 5,
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: MARKER_COLOR
  },
  markerInactive: {
    backgroundColor: colors.lightgray
  },
  markerSelected: {
    borderWidth: 1,
    borderColor: '#fff'
  }
});
